/**
 * Settings → Mailboxes (spec FR-1.5).
 *
 * A tenant admin connects a mailbox, links it to one scanner, and every document
 * sent from that mailbox is ingested as if it had been scanned on that device.
 * Three pieces:
 *
 * - the **list**, with health in the row — because a mailbox that has stopped
 *   working is invisible everywhere else in the product;
 * - the **connect wizard**, four steps, which signs in before it saves anything;
 * - the **activity drawer**, one row per email with the outcome of every
 *   attachment, which is what answers "why didn't my document come through?".
 *
 * Nothing here ever displays a credential: the API has no endpoint that returns
 * one. The wizard collects a secret, posts it, and forgets it.
 */
import { useCallback, useMemo, useState } from 'react';

import type {
  AttachmentType,
  MailboxConnectionView,
  MailboxMessage,
  MailboxOutcome,
  MailboxStatus,
  MailProvider,
  Scanner,
} from '../../api/schema';
import {
  Button,
  Drawer,
  EmptyState,
  ErrorState,
  IconButton,
  KpiTile,
  Modal,
  PasswordInput,
  Select,
  Skeleton,
  Toggle,
  Tooltip,
  useToast,
} from '../../components';
import { usePolling } from '../../lib/hooks';
import { useLoadState } from '../../lib/useLoadState';
import { fmtDate, relativeAge, reasonOf } from './format';
import { useSettingsApi } from './settingsApi';
import './settings.css';

const PROVIDER_LABEL: Record<MailProvider, string> = {
  microsoft_graph: 'Microsoft 365',
  imap: 'IMAP',
};

/** Status as words an admin can act on, not the enum value. */
const STATUS_LABEL: Record<MailboxStatus, string> = {
  active: 'Watching',
  paused: 'Paused',
  error: 'Sign-in failed',
  archived: 'Disconnected',
};

const OUTCOME_LABEL: Record<MailboxOutcome, string> = {
  ingested: 'Brought in',
  duplicate: 'Already held',
  skipped: 'Skipped',
  failed: 'Failed',
};

/** How many days ahead of a client-secret expiry we start warning. */
const EXPIRY_WARNING_DAYS = 30;

/**
 * How often the screen re-reads itself while a mailbox is being watched.
 *
 * Mail arrives on the server's schedule, not on a click: the poller checks every
 * 30 seconds and a document can appear at any moment. Without this the counters,
 * the "checked" column and the activity trail are frozen at whatever they were
 * when the page loaded, and the only way to see your own mail arriving is to hit
 * refresh — which makes a live feature look broken.
 *
 * Shorter than the server's own interval so a new document shows up within a few
 * seconds of being ingested, and cheap: one small GET returning a handful of rows.
 */
const MAILBOX_POLL_MS = 10_000;

const BACKFILL_OPTIONS = [
  { value: '0', label: 'New mail only (recommended)' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days (maximum)' },
];

const WIZARD_STEPS = ['Provider', 'Sign in', 'Name & scanner', 'What to bring in'] as const;

/**
 * The activity filters, as the *answers* an admin is looking for rather than one
 * chip per stored outcome.
 *
 * "Already held" is grouped with "Skipped" on purpose: a duplicate we recognised
 * instead of re-filing did not become a new document, which is the same answer as
 * one we rejected. Splitting them left duplicates reachable only under "All", so
 * an attachment could be missing from every count on the screen and still be
 * sitting in the list. The per-row badge keeps saying which it was.
 */
const ACTIVITY_FILTERS: readonly {
  key: string;
  label: string;
  outcomes?: readonly MailboxOutcome[];
}[] = [
  { key: 'all', label: 'All' },
  { key: 'ingested', label: 'Brought in', outcomes: ['ingested'] },
  { key: 'skipped', label: 'Skipped', outcomes: ['skipped', 'duplicate'] },
  { key: 'failed', label: 'Failed', outcomes: ['failed'] },
];

/**
 * The attachment-type choice, as presets rather than a checkbox per MIME family.
 * Almost every mailbox wants one of these four, and a preset states the intent
 * ("paperwork, not photos") in a way a list of ticks does not. The API stores the
 * resolved list, so offering finer control later needs no data change.
 */
const ATTACHMENT_PRESETS: readonly { value: string; label: string; types: AttachmentType[] }[] = [
  { value: 'pdf', label: 'PDFs only', types: ['pdf'] },
  {
    value: 'pdf_scans',
    label: 'PDFs and scans (TIFF) — recommended',
    types: ['pdf', 'tiff'],
  },
  {
    value: 'pdf_scans_photos',
    label: 'PDFs, scans and photos (JPEG, PNG)',
    types: ['pdf', 'tiff', 'image'],
  },
  {
    value: 'everything',
    label: 'Everything we accept, including text files',
    types: ['pdf', 'tiff', 'image', 'text'],
  },
];

const DEFAULT_PRESET = 'pdf_scans';

/** The photo floor, in the units an admin thinks in. */
// Labelled in KB up to a megabyte, matching how a skip reason states the same
// number in the activity drawer — one setting should not read as "0.25 MB" in one
// place and "256 KB" in another.
const IMAGE_FLOOR_OPTIONS = [
  { value: '102400', label: '100 KB — skips signatures and icons (recommended)' },
  { value: '262144', label: '256 KB — also skips small photos' },
  { value: '524288', label: '512 KB — only sizeable photos count as documents' },
  { value: '1048576', label: '1 MB — strictest' },
  { value: '0', label: 'No minimum — bring in every image' },
];

interface ProviderOption {
  value: MailProvider;
  title: string;
  description: string;
  badge?: string;
  badgeClass?: string;
}

/**
 * The two providers that can actually be connected. Gmail is not a third entry:
 * it is reached through IMAP with an app password, which the IMAP option says in
 * so many words — a row that cannot be selected is furniture, not information.
 */
const PROVIDER_OPTIONS: readonly ProviderOption[] = [
  {
    value: 'microsoft_graph',
    title: 'Microsoft 365 / Outlook',
    description:
      'Needs a directory (tenant) ID, application (client) ID and client secret from an app registration with Mail.Read granted.',
    badge: 'Recommended',
    badgeClass: 'set-badge--ok',
  },
  {
    value: 'imap',
    title: 'Any other mail provider (IMAP)',
    description:
      'Server, port, username and password — or an app password. Covers Gmail, Zoho, on-premise Exchange and hosted mail.',
  },
];

/**
 * Row-action icons. Local to this screen rather than added to `shell/icons.tsx`,
 * which is the navigation set — these three are table furniture and belong with
 * the table that uses them. Every one is decorative: the accessible name lives on
 * the `IconButton`, which requires it.
 */
function ActivityIcon(): React.JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <circle cx="8" cy="8" r="6.5" />
      <polyline points="8 4.5 8 8 10.5 9.5" />
    </svg>
  );
}

function CheckNowIcon(): React.JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9" />
      <polyline points="13.5 2 13.5 5 10.5 5" />
    </svg>
  );
}

function PauseIcon(): React.JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <rect x="4.5" y="3.5" width="2.6" height="9" rx="0.8" />
      <rect x="8.9" y="3.5" width="2.6" height="9" rx="0.8" />
    </svg>
  );
}

function PlayIcon(): React.JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5.2 3.6a.8.8 0 0 1 1.2-.7l6 4.4a.8.8 0 0 1 0 1.4l-6 4.4a.8.8 0 0 1-1.2-.7Z" />
    </svg>
  );
}

function RemoveIcon(): React.JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path d="M2.5 4h11" />
      <path d="M6.5 4V2.5h3V4" />
      <path d="M4 4l.7 9a1 1 0 0 0 1 .9h4.6a1 1 0 0 0 1-.9L12 4" />
      <path d="M6.7 6.5v5M9.3 6.5v5" />
    </svg>
  );
}

function FixIcon(): React.JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <circle cx="5.5" cy="10.5" r="2.6" />
      <path d="M7.4 8.6 13 3l0 0" />
      <path d="M10.4 5.6l1.4 1.4M12 4l1.4 1.4" />
    </svg>
  );
}

interface MailboxesData {
  mailboxes: MailboxConnectionView[];
  scanners: Scanner[];
}

/**
 * Open the browser's date picker for a field, when it has one.
 *
 * Clicking a `type="date"` input only opens the calendar if you hit the small
 * indicator glyph; clicking the text puts you in typing mode. Since nobody wants
 * to type `dd/mm/yyyy`, the whole field becomes the button. Guarded because
 * `showPicker` is unsupported in older Safari and throws if the call is not
 * inside a user gesture — the field still accepts typing either way.
 */
function openDatePicker(field: HTMLInputElement): void {
  const withPicker = field as HTMLInputElement & { showPicker?: () => void };
  if (typeof withPicker.showPicker !== 'function') return;
  try {
    withPicker.showPicker();
  } catch {
    // No picker available; typing into the field still works.
    return;
  }
}

/**
 * Attachments that did not become a new document — rejected by a filter, or
 * recognised as one we already hold.
 *
 * Counted together because the number answers one question ("what didn't come
 * in?") and a reader who sees "5 skipped" while six attachments are missing has
 * been told something false. The two are still stored and badged separately.
 */
function notBroughtIn(mailbox: MailboxConnectionView): number {
  return mailbox.counters.skipped + mailbox.counters.duplicates;
}

/** Days until `iso`, or null when it is unset or already past. */
function daysUntil(iso: string | null | undefined, now: number = Date.now()): number | null {
  if (iso == null) return null;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return null;
  const days = Math.ceil((at - now) / 86_400_000);
  return days >= 0 ? days : null;
}

/** The one-line health summary a row shows, and how loudly. */
function healthOf(mailbox: MailboxConnectionView): {
  tone: 'ok' | 'warn' | 'bad' | 'idle';
  label: string;
} {
  if (mailbox.status === 'error') return { tone: 'bad', label: STATUS_LABEL.error };
  if (mailbox.status === 'paused') return { tone: 'idle', label: STATUS_LABEL.paused };
  if (mailbox.status === 'archived') return { tone: 'idle', label: STATUS_LABEL.archived };
  const expiring = daysUntil(mailbox.secret_expires_at);
  if (expiring !== null && expiring <= EXPIRY_WARNING_DAYS) {
    return { tone: 'warn', label: `Secret expires in ${String(expiring)} d` };
  }
  return { tone: 'ok', label: STATUS_LABEL.active };
}

// Folder is not shown: every mailbox watches Sent, so a column repeating that
// for every row is noise. It is still on the record and in the activity drawer.
// The actions column fits three 32px icon buttons and their gaps.
const ROW_COLUMNS = '1.9fr .9fr 1.15fr 1.05fr .85fr .75fr 180px';

export function MailboxesScreen(): React.JSX.Element {
  const { toast } = useToast();
  const settingsApi = useSettingsApi();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [activityFor, setActivityFor] = useState<MailboxConnectionView | null>(null);
  const [disconnecting, setDisconnecting] = useState<MailboxConnectionView | null>(null);
  const [rotating, setRotating] = useState<MailboxConnectionView | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchAll = useCallback(
    async (signal: AbortSignal): Promise<MailboxesData> => {
      const [mailboxes, scanners] = await Promise.all([
        settingsApi.mailboxes.list(signal),
        settingsApi.scanners.list(signal),
      ]);
      return { mailboxes: mailboxes.items, scanners: scanners.items };
    },
    [settingsApi],
  );
  const state = useLoadState(fetchAll);
  const reload = state.status === 'ready' || state.status === 'error' ? state.reload : undefined;

  const scannerLabel = useCallback(
    (id: string): string => {
      if (state.status !== 'ready') return id;
      const scanner = state.data.scanners.find((s) => s.id === id);
      return scanner ? scanner.device_id : 'Unknown scanner';
    },
    [state],
  );

  const totals = useMemo(() => {
    if (state.status !== 'ready') return { connected: 0, documents: 0, skipped: 0, attention: 0 };
    const live = state.data.mailboxes;
    return {
      connected: live.length,
      documents: live.reduce((sum, m) => sum + m.counters.documents_ingested, 0),
      skipped: live.reduce((sum, m) => sum + notBroughtIn(m), 0),
      attention: live.filter((m) => healthOf(m).tone === 'bad' || healthOf(m).tone === 'warn').length,
    };
  }, [state]);

  const runAction = useCallback(
    async (mailbox: MailboxConnectionView, what: string, action: () => Promise<unknown>) => {
      setBusyId(mailbox.id);
      try {
        await action();
        toast(what);
        reload?.();
      } catch (error) {
        toast(reasonOf(error, `Could not ${what.toLowerCase()} — try again`), { tone: 'error' });
      } finally {
        setBusyId(null);
      }
    },
    [toast, reload],
  );

  // Poll only while something is actually being watched: a screen of paused or
  // failed mailboxes has nothing that can change on its own, so the timer stops.
  const watching = state.status === 'ready' && state.data.mailboxes.some((m) => m.status === 'active');
  const refresh = state.status === 'ready' ? state.refresh : undefined;
  usePolling(watching, MAILBOX_POLL_MS, state, () => {
    refresh?.();
  });

  if (state.status === 'loading') {
    return (
      <section className="set-wrap" aria-label="Mailboxes">
        <Skeleton height={92} />
        <Skeleton height={240} />
        <p className="set-loading-caption">Loading mailboxes…</p>
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="set-wrap" aria-label="Mailboxes">
        <ErrorState
          title="We couldn't load your mailboxes"
          body="The settings service didn't respond. Any connected mailboxes keep running — this only affects this view."
          traceId={state.traceId ?? undefined}
          actions={
            <Button onClick={state.reload} variant="primary">
              Retry
            </Button>
          }
        />
      </section>
    );
  }

  const { mailboxes, scanners } = state.data;
  const linkedScanners = new Set(mailboxes.map((m) => m.scanner_id));
  const needsAttention = mailboxes.filter((m) => ['bad', 'warn'].includes(healthOf(m).tone));
  const firstProblem = needsAttention[0];

  return (
    <section className="set-wrap" aria-label="Mailboxes">
      {firstProblem !== undefined ? (
        <div className="set-banner" role="status">
          <span className="set-dot set-dot--warn" aria-hidden="true" />
          <span>
            {needsAttention.length === 1
              ? `${firstProblem.address} needs attention — ${healthOf(firstProblem).label.toLowerCase()}.`
              : `${String(needsAttention.length)} mailboxes need attention.`}{' '}
            While a mailbox is stopped, documents sent from it are not arriving.
          </span>
        </div>
      ) : null}

      {mailboxes.length > 0 ? (
        <div className="set-two set-two--1-1" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          <KpiTile label="Connected" value={String(totals.connected)} />
          <KpiTile label="Documents brought in" value={totals.documents.toLocaleString()} />
          <KpiTile label="Attachments skipped" value={totals.skipped.toLocaleString()} />
          <KpiTile label="Need attention" value={String(totals.attention)} />
        </div>
      ) : null}

      <div className="set-card">
        <div className="set-card__head">
          <h2 className="set-card__title">Connected mailboxes</h2>
          <span className="set-card__aside set-card__spacer">
            checked every 30 seconds
          </span>
          <Button variant="primary" size="sm" onClick={() => { setWizardOpen(true); }}>
            + Connect mailbox
          </Button>
        </div>

        {mailboxes.length === 0 ? (
          <EmptyState
            title="Connect your first mailbox"
            body="Docsight can watch a mailbox and bring in the documents you send from it — invoices, despatch notes, statements — with no change to how your team works. Each mailbox is linked to one scanner so its documents are grouped and located like a scan."
            actions={
              <Button variant="primary" onClick={() => { setWizardOpen(true); }}>
                Connect a mailbox
              </Button>
            }
          />
        ) : (
          <div className="set-scroll">
            <div className="set-grid set-grid__head" role="row" style={{ gridTemplateColumns: ROW_COLUMNS }}>
              <span role="columnheader">Mailbox</span>
              <span role="columnheader">Provider</span>
              <span role="columnheader">Scanner</span>
              <span role="columnheader">Status</span>
              <span role="columnheader">Checked</span>
              <span role="columnheader">Documents</span>
              <span role="columnheader" style={{ textAlign: 'right' }}>
                Actions
              </span>
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Connected mailboxes">
              {mailboxes.map((mailbox) => {
                const health = healthOf(mailbox);
                return (
                  <li className="set-grid set-grid__row" key={mailbox.id} style={{ gridTemplateColumns: ROW_COLUMNS }}>
                    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <span className="set-mono" style={{ fontSize: 11.5 }}>
                        {mailbox.address}
                      </span>
                      <span className="set-hint">{mailbox.name}</span>
                    </span>
                    <span style={{ color: 'var(--t2)' }}>{PROVIDER_LABEL[mailbox.provider]}</span>
                    <span className="set-mono" style={{ fontSize: 11.5 }}>
                      {scannerLabel(mailbox.scanner_id)}
                    </span>
                    <span className="set-status">
                      <span className={`set-dot set-dot--${health.tone}`} aria-hidden="true" />
                      {health.label}
                    </span>
                    <span style={{ color: 'var(--t3)' }}>{relativeAge(mailbox.health.last_polled_at)}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {mailbox.counters.documents_ingested.toLocaleString()}
                    </span>
                    <span className="set-rowactions">
                      <Tooltip content="Activity" visualOnly>
                        <IconButton
                          label={`Activity for ${mailbox.address}`}
                          onClick={() => { setActivityFor(mailbox); }}
                        >
                          <ActivityIcon />
                        </IconButton>
                      </Tooltip>
                      <MailboxMenu
                        mailbox={mailbox}
                        busy={busyId === mailbox.id}
                        onSync={() => {
                          void runAction(mailbox, 'Checking now', () =>
                            settingsApi.mailboxes.sync(mailbox.id),
                          );
                        }}
                        onPause={() => {
                          const pausing = mailbox.status === 'active';
                          void runAction(mailbox, pausing ? 'Paused' : 'Resumed', () =>
                            settingsApi.mailboxes.update(mailbox.id, {
                              status: pausing ? 'paused' : 'active',
                            }),
                          );
                        }}
                        onRotate={() => { setRotating(mailbox); }}
                        onDisconnect={() => { setDisconnecting(mailbox); }}
                      />
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <p className="set-card__foot">
          Credentials are encrypted at rest and never shown again once saved. Docsight only ever reads — it never
          sends, moves, marks as read or deletes anything in your mailbox.
        </p>
      </div>

      <ConnectMailboxWizard
        open={wizardOpen}
        scanners={scanners}
        linkedScanners={linkedScanners}
        onClose={() => { setWizardOpen(false); }}
        onConnected={(mailbox) => {
          setWizardOpen(false);
          toast(`${mailbox.address} connected — checking it now`);
          reload?.();
        }}
      />

      <MailboxActivityDrawer mailbox={activityFor} onClose={() => { setActivityFor(null); }} />

      <ReplaceCredentialsModal
        mailbox={rotating}
        onClose={() => { setRotating(null); }}
        onReplaced={() => {
          setRotating(null);
          toast('Credentials replaced — the mailbox is active again');
          reload?.();
        }}
      />

      <Modal
        open={disconnecting !== null}
        onClose={() => { setDisconnecting(null); }}
        title={`Disconnect ${disconnecting?.address ?? ''}?`}
        dismissible
        footer={
          <div className="ds-modal__footer">
            <Button variant="secondary" onClick={() => { setDisconnecting(null); }}>
              Keep it connected
            </Button>
            <Button
              variant="destructive"
              loading={busyId === disconnecting?.id}
              onClick={() => {
                const mailbox = disconnecting;
                if (mailbox === null) return;
                void runAction(mailbox, 'Disconnected', async () => {
                  await settingsApi.mailboxes.disconnect(mailbox.id);
                  setDisconnecting(null);
                });
              }}
            >
              Disconnect
            </Button>
          </div>
        }
      >
        <div className="ds-modal__body">
          <p style={{ marginTop: 0 }}>
            We&apos;ll stop checking this mailbox and delete the stored credentials. Its activity history is removed
            too.
          </p>
          <p>
            <strong>The {disconnecting?.counters.documents_ingested.toLocaleString() ?? 0} documents it already
            brought in are kept</strong> — they stay exactly where they are.
          </p>
        </div>
      </Modal>
    </section>
  );
}

/**
 * The per-row actions, as icon buttons.
 *
 * Icon-only saves a table column, but only works because every one carries an
 * accessible name naming *which* mailbox it acts on — three identical "Remove"
 * buttons in a list are useless to a screen reader, and a bare glyph is useless
 * to everyone until hovered.
 */
function MailboxMenu({
  mailbox,
  busy,
  onSync,
  onPause,
  onRotate,
  onDisconnect,
}: {
  mailbox: MailboxConnectionView;
  busy: boolean;
  onSync: () => void;
  onPause: () => void;
  onRotate: () => void;
  onDisconnect: () => void;
}): React.JSX.Element {
  // An errored mailbox gets one obvious action — replacing the credential is the
  // only thing that fixes it, and offering "check now" would just fail again.
  const remove = (
    <Tooltip content="Disconnect" visualOnly>
      <IconButton
        label={`Disconnect ${mailbox.address}`}
        className="set-iconbtn--bad"
        disabled={busy}
        onClick={onDisconnect}
      >
        <RemoveIcon />
      </IconButton>
    </Tooltip>
  );

  // A mailbox we stopped has one thing wrong with it and one thing that fixes
  // it. Offering "check now" would just fail again with the same credential.
  if (mailbox.status === 'error') {
    return (
      <>
        <Tooltip content="Replace credentials" visualOnly>
          <IconButton
            label={`Replace the credentials for ${mailbox.address}`}
            disabled={busy}
            onClick={onRotate}
          >
            <FixIcon />
          </IconButton>
        </Tooltip>
        {remove}
      </>
    );
  }

  // Paused: resume is the only forward action. "Check now" is deliberately
  // absent — the API refuses a sync on a paused mailbox, and offering a button
  // that returns an error is worse than not offering it.
  if (mailbox.status !== 'active') {
    return (
      <>
        <Tooltip content="Resume" visualOnly>
          <IconButton
            label={`Resume watching ${mailbox.address}`}
            disabled={busy}
            onClick={onPause}
          >
            <PlayIcon />
          </IconButton>
        </Tooltip>
        {remove}
      </>
    );
  }

  return (
    <>
      <Tooltip content="Check now" visualOnly>
        <IconButton
          label={`Check ${mailbox.address} now`}
          disabled={busy}
          onClick={onSync}
        >
          <CheckNowIcon />
        </IconButton>
      </Tooltip>
      <Tooltip content="Pause" visualOnly>
        <IconButton
          label={`Pause watching ${mailbox.address}`}
          disabled={busy}
          onClick={onPause}
        >
          <PauseIcon />
        </IconButton>
      </Tooltip>
      {remove}
    </>
  );
}

interface WizardProps {
  open: boolean;
  scanners: Scanner[];
  linkedScanners: Set<string>;
  onClose: () => void;
  onConnected: (mailbox: MailboxConnectionView) => void;
}

/**
 * The four-step connect flow. Step order matters: the credentials are collected
 * and *proved* before the admin is asked to name it and pick a scanner, so
 * nobody fills in four screens only to find the password was wrong.
 *
 * The folder is not offered: every mailbox watches Sent. The API still
 * accepts one, so exposing the choice later is a UI change alone.
 */
function ConnectMailboxWizard({ open, scanners, linkedScanners, onClose, onConnected }: WizardProps): React.JSX.Element {
  const settingsApi = useSettingsApi();
  const { toast } = useToast();

  const [step, setStep] = useState(1);
  const [provider, setProvider] = useState<MailProvider>('microsoft_graph');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [secret, setSecret] = useState('');
  const [azureTenantId, setAzureTenantId] = useState('');
  const [azureClientId, setAzureClientId] = useState('');
  const [secretExpiresAt, setSecretExpiresAt] = useState('');
  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState('993');
  const [imapUseSsl, setImapUseSsl] = useState(true);
  const [scannerId, setScannerId] = useState('');
  const [backfill, setBackfill] = useState('0');
  const [preset, setPreset] = useState(DEFAULT_PRESET);
  const [imageFloor, setImageFloor] = useState('102400');
  const [skipInline, setSkipInline] = useState(true);
  const [busy, setBusy] = useState(false);

  const reset = useCallback(() => {
    setStep(1);
    setProvider('microsoft_graph');
    setName('');
    setAddress('');
    setSecret('');
    setAzureTenantId('');
    setAzureClientId('');
    setSecretExpiresAt('');
    setImapHost('');
    setImapPort('993');
    setImapUseSsl(true);
    setScannerId('');
    setBackfill('0');
    setPreset(DEFAULT_PRESET);
    setImageFloor('102400');
    setSkipInline(true);
  }, []);

  const close = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // The photo controls only make sense when photos are being brought in at all —
  // showing a "smallest photo" dropdown next to "PDFs only" invites the reader to
  // wonder which one wins.
  const includesPhotos = (
    ATTACHMENT_PRESETS.find((option) => option.value === preset)?.types ?? []
  ).includes('image');

  const credentialsReady =
    address.trim() !== '' &&
    secret.trim() !== '' &&
    (provider === 'microsoft_graph'
      ? azureTenantId.trim() !== '' && azureClientId.trim() !== ''
      : imapHost.trim() !== '');

  const submit = useCallback(() => {
    if (!credentialsReady || scannerId === '' || name.trim() === '') return;
    setBusy(true);
    void (async () => {
      try {
        // One call: the API signs in, reads the folder and only then stores the
        // mailbox — so a failure here means nothing was created.
        const created = await settingsApi.mailboxes.connect({
          name: name.trim(),
          provider,
          address: address.trim(),
          // Always the Sent folder: the API resolves whatever this server calls
          // it (RFC 6154 special-use for IMAP, the well-known name for Graph).
          folder: null,
          scanner_id: scannerId,
          secret,
          ...(provider === 'microsoft_graph'
            ? {
                azure_tenant_id: azureTenantId.trim(),
                azure_client_id: azureClientId.trim(),
                secret_expires_at: secretExpiresAt === '' ? null : new Date(secretExpiresAt).toISOString(),
              }
            : {
                imap_host: imapHost.trim(),
                imap_port: Number(imapPort) || 993,
                imap_use_ssl: imapUseSsl,
              }),
          backfill_days: Number(backfill),
          filters: {
            attachment_types:
              ATTACHMENT_PRESETS.find((p) => p.value === preset)?.types ?? ['pdf', 'tiff'],
            min_image_size_bytes: Number(imageFloor),
            skip_inline: skipInline,
            filename_denylist: ['.p7s', '.p7m', '.ics', '.vcf', '.asc'],
            recipient_allowlist: [],
          },
        });
        reset();
        onConnected(created);
      } catch (error) {
        // The message from the API names what was actually wrong — a rejected
        // credential, a missing folder, a scanner already in use.
        toast(reasonOf(error, 'We could not connect that mailbox — check the details and try again'), {
          tone: 'error',
        });
      } finally {
        setBusy(false);
      }
    })();
  }, [
    credentialsReady, scannerId, name, provider, address, secret, azureTenantId, azureClientId,
    secretExpiresAt, imapHost, imapPort, imapUseSsl, backfill, preset, imageFloor, skipInline,
    settingsApi, onConnected, reset, toast,
  ]);

  const canContinue =
    (step === 1) ||
    (step === 2 && credentialsReady) ||
    (step === 3 && scannerId !== '' && name.trim() !== '') ||
    step === 4;

  return (
    <Modal
      open={open}
      onClose={close}
      title="Connect mailbox"
      // 620px: four steps of form need the room, and the narrower default made
      // the provider cards wrap onto three lines each.
      width="xxl"
      dismissible
      footer={
        <div className="ds-modal__footer">
          <span style={{ marginRight: 'auto', fontSize: 11.5, color: 'var(--t3)' }}>
            Step {String(step)} of 4
          </span>
          <Button variant="secondary" disabled={step === 1 || busy} onClick={() => { setStep((s) => s - 1); }}>
            Back
          </Button>
          {step < 4 ? (
            <Button variant="primary" disabled={!canContinue} onClick={() => { setStep((s) => s + 1); }}>
              Continue
            </Button>
          ) : (
            <Button variant="primary" loading={busy} disabled={!credentialsReady || scannerId === ''} onClick={submit}>
              Connect mailbox
            </Button>
          )}
        </div>
      }
    >
      <div className="ds-modal__body">
        {/* Where the admin is, and how far there is to go. Non-interactive on
            purpose: a step cannot be jumped to before the one before it has the
            answers it needs. */}
        <ol className="set-steps" aria-label="Connect mailbox steps">
          {WIZARD_STEPS.map((label, index) => {
            const number = index + 1;
            const stateClass =
              number === step ? 'set-step set-step--on' : number < step ? 'set-step set-step--done' : 'set-step';
            return (
              <li key={label}>
                <span className={stateClass} aria-current={number === step ? 'step' : undefined}>
                  <span className="set-step__no" aria-hidden="true">
                    {number}
                  </span>
                  {label}
                </span>
              </li>
            );
          })}
        </ol>

        {step === 1 ? (
          <>
            <p className="set-hint" style={{ marginTop: 0 }}>
              Where does this mailbox live? Your IT team supplies the details in step 2.
            </p>
            <div className="ds-radiogroup" role="radiogroup" aria-label="Mail provider">
              {PROVIDER_OPTIONS.map((option) => {
                const selected = provider === option.value;
                return (
                  <label
                    key={option.value}
                    className={selected ? 'set-optrow set-optrow--on' : 'set-optrow'}
                  >
                    <input
                      type="radio"
                      name="mailbox-provider"
                      checked={selected}
                      // The visible title is split across styled spans, so the
                      // control carries its own name and points at the
                      // description rather than relying on label nesting.
                      aria-label={option.title}
                      aria-describedby={`provider-${option.value}-desc`}
                      onChange={() => { setProvider(option.value); }}
                    />
                    <span>
                      <span className="set-optrow__head">
                        <span className="set-optrow__name">{option.title}</span>
                        {option.badge !== undefined ? (
                          <span className={`set-badge ${option.badgeClass ?? ''}`}>{option.badge}</span>
                        ) : null}
                      </span>
                      <span className="set-optrow__desc" id={`provider-${option.value}-desc`}>
                        {option.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </>
        ) : null}

        {step === 2 ? (
          <>
            <div className="ds-field">
              <label className="ds-field__label" htmlFor="mb-address">
                Mailbox to watch
              </label>
              <input
                id="mb-address"
                className="ds-input"
                placeholder="ops@yourcompany.com"
                value={address}
                onChange={(e) => { setAddress(e.currentTarget.value); }}
              />
            </div>
            {provider === 'microsoft_graph' ? (
              <>
                {/* Client secret, application id, directory id — the order the
                    values are copied out of the Azure portal, so the three fields
                    are filled in one pass rather than jumping around a page. */}
                <div className="ds-field" style={{ marginTop: 12 }}>
                  <label className="ds-field__label" htmlFor="mb-secret">
                    Client secret
                  </label>
                  <PasswordInput
                    id="mb-secret"
                    className="ds-input"
                    value={secret}
                    onChange={(e) => { setSecret(e.currentTarget.value); }}
                  />
                  <span className="ds-field__hint">Encrypted when saved, and never shown again.</span>
                </div>
                <div className="ds-field" style={{ marginTop: 12 }}>
                  <label className="ds-field__label" htmlFor="mb-client">
                    Application (client) ID
                  </label>
                  <input
                    id="mb-client"
                    className="ds-input set-mono"
                    value={azureClientId}
                    onChange={(e) => { setAzureClientId(e.currentTarget.value); }}
                  />
                </div>
                <div className="ds-field" style={{ marginTop: 12 }}>
                  <label className="ds-field__label" htmlFor="mb-tenant">
                    Directory (tenant) ID
                  </label>
                  <input
                    id="mb-tenant"
                    className="ds-input set-mono"
                    value={azureTenantId}
                    onChange={(e) => { setAzureTenantId(e.currentTarget.value); }}
                  />
                </div>
                <div className="ds-field" style={{ marginTop: 12 }}>
                  <label className="ds-field__label" htmlFor="mb-expires">
                    Secret expires (optional)
                  </label>
                  <input
                    id="mb-expires"
                    type="date"
                    className="ds-input set-datefield"
                    value={secretExpiresAt}
                    onChange={(e) => { setSecretExpiresAt(e.currentTarget.value); }}
                    onClick={(e) => { openDatePicker(e.currentTarget); }}
                  />
                  <span className="ds-field__hint">We&apos;ll warn you 30 days before it runs out.</span>
                </div>
                <div className="set-banner set-banner--info" style={{ marginTop: 14 }}>
                  <span className="set-dot set-dot--idle" aria-hidden="true" />
                  <span>
                    Ask whoever runs your Microsoft tenant to grant <span className="set-mono">Mail.Read</span> as an
                    application permission with admin consent, and to restrict the app to this one mailbox with an
                    application access policy.
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="set-two set-two--1-1" style={{ marginTop: 12 }}>
                  <div className="ds-field">
                    <label className="ds-field__label" htmlFor="mb-host">
                      Server
                    </label>
                    <input
                      id="mb-host"
                      className="ds-input set-mono"
                      placeholder="imap.yourprovider.com"
                      value={imapHost}
                      onChange={(e) => { setImapHost(e.currentTarget.value); }}
                    />
                  </div>
                  <div className="ds-field">
                    <label className="ds-field__label" htmlFor="mb-port">
                      Port
                    </label>
                    <input
                      id="mb-port"
                      className="ds-input set-mono"
                      value={imapPort}
                      onChange={(e) => { setImapPort(e.currentTarget.value); }}
                    />
                  </div>
                </div>
                <div className="ds-field" style={{ marginTop: 12 }}>
                  <label className="ds-field__label" htmlFor="mb-pass">
                    Password or app password
                  </label>
                  <PasswordInput
                    id="mb-pass"
                    className="ds-input"
                    value={secret}
                    onChange={(e) => { setSecret(e.currentTarget.value); }}
                  />
                  <span className="ds-field__hint">
                    Gmail, Yahoo and most hosted mail need an app password rather than the account password.
                  </span>
                </div>
                <div className="ds-check" style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12 }}>
                  <Toggle checked={imapUseSsl} onChange={setImapUseSsl} label="Use SSL/TLS (port 993)" />
                  <span style={{ fontSize: 12.5 }} aria-hidden="true">
                    Use SSL/TLS (port 993)
                  </span>
                </div>
              </>
            )}
          </>
        ) : null}

        {step === 3 ? (
          <>
            <div className="ds-field">
              <label className="ds-field__label" htmlFor="mb-name">
                Name this connection
              </label>
              <input
                id="mb-name"
                className="ds-input"
                placeholder="e.g. Invoices out — Manchester"
                value={name}
                onChange={(e) => { setName(e.currentTarget.value); }}
              />
            </div>
            <div className="set-banner set-banner--info" style={{ marginTop: 12 }}>
              <span className="set-dot" aria-hidden="true" />
              <span>
                We watch the mailbox&apos;s <strong>Sent</strong> folder — whatever this server calls
                it. Documents you send out are captured; nothing in the inbox is touched.
              </span>
            </div>
            <div className="ds-field" style={{ marginTop: 12 }}>
              <label className="ds-field__label" htmlFor="mb-scanner">
                Bring documents in as
              </label>
              <Select
                id="mb-scanner"
                value={scannerId}
                onChange={(e) => { setScannerId(e.currentTarget.value); }}
                options={[
                  { value: '', label: 'Choose a scanner…' },
                  ...scanners.map((scanner) => ({
                    value: scanner.id,
                    label: linkedScanners.has(scanner.id)
                      ? `${scanner.device_id} — already linked to a mailbox`
                      : scanner.device_id,
                    disabled: linkedScanners.has(scanner.id),
                  })),
                ]}
              />
              <span className="ds-field__hint">
                One mailbox, one scanner — that&apos;s what lets the dashboards tell their documents apart. Add a
                scanner in Locations &amp; scanners if you need a new one.
              </span>
            </div>
          </>
        ) : null}

        {step === 4 ? (
          <>
            <div className="ds-field">
              <label className="ds-field__label" htmlFor="mb-backfill">
                Start from
              </label>
              <Select
                id="mb-backfill"
                value={backfill}
                onChange={(e) => { setBackfill(e.currentTarget.value); }}
                options={BACKFILL_OPTIONS}
              />
              <span className="ds-field__hint">
                Importing history counts towards your document volume and puts every matching attachment through
                classification.
              </span>
            </div>
            <div className="ds-field" style={{ marginTop: 12 }}>
              <label className="ds-field__label" htmlFor="mb-types">
                Attachment types to bring in
              </label>
              <Select
                id="mb-types"
                value={preset}
                onChange={(e) => { setPreset(e.currentTarget.value); }}
                options={ATTACHMENT_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
              />
              <span className="ds-field__hint">
                Anything else in an email — signatures, calendar invites, contact cards — is left
                alone.
              </span>
            </div>
            {includesPhotos ? (
              <div className="ds-field" style={{ marginTop: 12 }}>
                <label className="ds-field__label" htmlFor="mb-imagefloor">
                  Smallest photo to treat as a document
                </label>
                <Select
                  id="mb-imagefloor"
                  value={imageFloor}
                  onChange={(e) => { setImageFloor(e.currentTarget.value); }}
                  options={IMAGE_FLOOR_OPTIONS}
                />
                <span className="ds-field__hint">
                  Photos only — a PDF or scan is brought in whatever its size.
                </span>
              </div>
            ) : null}
            {includesPhotos ? (
              <div
                className="ds-check"
                style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14 }}
              >
                <Toggle
                  checked={skipInline}
                  onChange={setSkipInline}
                  label="Skip images embedded in the message body"
                />
                <span style={{ fontSize: 12.5 }} aria-hidden="true">
                  Skip images embedded in the message body
                </span>
              </div>
            ) : null}
            <p className="set-card__foot" style={{ marginTop: 14 }}>
              When you connect, we sign in and check the folder before saving anything — so if the details are wrong,
              nothing is created and we&apos;ll tell you why.
            </p>
          </>
        ) : null}
      </div>
    </Modal>
  );
}

/** Replace a stored credential — the only fix for a mailbox we can't sign in to. */
function ReplaceCredentialsModal({
  mailbox,
  onClose,
  onReplaced,
}: {
  mailbox: MailboxConnectionView | null;
  onClose: () => void;
  onReplaced: () => void;
}): React.JSX.Element {
  const settingsApi = useSettingsApi();
  const { toast } = useToast();
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(() => {
    if (mailbox === null || secret.trim() === '') return;
    setBusy(true);
    void (async () => {
      try {
        await settingsApi.mailboxes.replaceCredentials(mailbox.id, { secret });
        setSecret('');
        onReplaced();
      } catch (error) {
        toast(reasonOf(error, 'That credential was rejected — check it and try again'), { tone: 'error' });
      } finally {
        setBusy(false);
      }
    })();
  }, [mailbox, secret, settingsApi, onReplaced, toast]);

  return (
    <Modal
      open={mailbox !== null}
      onClose={() => { setSecret(''); onClose(); }}
      title="Replace credentials"
      dismissible
      footer={
        <div className="ds-modal__footer">
          <Button variant="secondary" onClick={() => { setSecret(''); onClose(); }}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={secret.trim() === ''} onClick={submit}>
            Test and save
          </Button>
        </div>
      }
    >
      <div className="ds-modal__body">
        {mailbox?.health.last_error != null ? (
          <div className="set-banner" style={{ marginTop: 0, marginBottom: 12 }}>
            <span className="set-dot set-dot--bad" aria-hidden="true" />
            <span>{mailbox.health.last_error}</span>
          </div>
        ) : null}
        <div className="ds-field">
          <label className="ds-field__label" htmlFor="mb-newsecret">
            {mailbox?.provider === 'microsoft_graph' ? 'New client secret' : 'New password or app password'}
          </label>
          <PasswordInput
            id="mb-newsecret"
            className="ds-input"
            value={secret}
            onChange={(e) => { setSecret(e.currentTarget.value); }}
          />
          <span className="ds-field__hint">
            We test it before replacing the stored one, so a mistyped secret leaves the mailbox exactly as it is.
          </span>
        </div>
      </div>
    </Modal>
  );
}

/** One row per email, with what happened to each attachment and why. */
function MailboxActivityDrawer({
  mailbox,
  onClose,
}: {
  mailbox: MailboxConnectionView | null;
  onClose: () => void;
}): React.JSX.Element {
  const settingsApi = useSettingsApi();
  const [filterKey, setFilterKey] = useState('all');
  const mailboxId = mailbox?.id ?? null;
  const selectedFilter = ACTIVITY_FILTERS.find((one) => one.key === filterKey) ?? ACTIVITY_FILTERS[0];
  const outcomes = selectedFilter?.outcomes;

  const fetchMessages = useCallback(
    async (signal: AbortSignal): Promise<MailboxMessage[]> => {
      if (mailboxId === null) return [];
      const page = await settingsApi.mailboxes.messages(mailboxId, { outcomes }, signal);
      return page.items;
    },
    [settingsApi, mailboxId, outcomes],
  );
  const state = useLoadState(fetchMessages);
  const refresh = state.status === 'ready' ? state.refresh : undefined;
  // Only while it is open, and only for a mailbox that is still being watched.
  usePolling(
    mailbox !== null && mailbox.status === 'active' && state.status === 'ready',
    MAILBOX_POLL_MS,
    state,
    () => {
      refresh?.();
    },
  );

  return (
    <Drawer
      open={mailbox !== null}
      onClose={onClose}
      title={mailbox?.address ?? 'Mailbox activity'}
      footer={
        <span className="set-hint">
          Activity is kept for 30 days. The documents themselves are kept for as long as your retention policy says.
        </span>
      }
    >
      {mailbox !== null ? (
        <>
          <p className="set-hint" style={{ marginTop: 0 }}>
            {mailbox.folder} → {mailbox.name} · {mailbox.counters.messages_seen.toLocaleString()} emails seen ·{' '}
            {mailbox.counters.documents_ingested.toLocaleString()} documents ·{' '}
            {notBroughtIn(mailbox).toLocaleString()} skipped
          </p>

          <div className="set-seg" role="group" aria-label="Filter activity" style={{ marginBottom: 12 }}>
            {ACTIVITY_FILTERS.map((option) => {
              const on = option.key === filterKey;
              return (
                <button
                  key={option.key}
                  type="button"
                  // The `--on` class is what makes the choice visible; `aria-pressed`
                  // alone told assistive tech and nobody else.
                  className={on ? 'set-seg__btn set-seg__btn--on' : 'set-seg__btn'}
                  aria-pressed={on}
                  onClick={() => { setFilterKey(option.key); }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          {state.status === 'loading' ? <Skeleton height={180} /> : null}
          {state.status === 'error' ? (
            <ErrorState title="We couldn't load the activity" body="Try again in a moment." />
          ) : null}
          {state.status === 'ready' && state.data.length === 0 ? (
            <EmptyState
              title="Nothing yet"
              body={
                mailbox.health.last_polled_at == null
                  ? "We haven't checked this mailbox yet — the first check runs within 30 seconds."
                  : 'No emails matching this filter in the last 30 days.'
              }
            />
          ) : null}
          {state.status === 'ready'
            ? state.data.map((message) => (
                <div className="set-card" key={message.id} style={{ marginBottom: 10, padding: 0 }}>
                  <div style={{ padding: '9px 12px', borderBottom: '1px solid var(--bd)', background: 'var(--hov)' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500 }}>{message.subject ?? '(no subject)'}</div>
                    <div className="set-hint">
                      {message.recipients.length > 0 ? `to ${message.recipients.join(', ')} · ` : ''}
                      {fmtDate(message.sent_at)}
                    </div>
                  </div>
                  {message.attachments.length === 0 ? (
                    <div className="set-hint" style={{ padding: '9px 12px' }}>
                      No attachments on this email.
                    </div>
                  ) : (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {message.attachments.map((attachment, index) => (
                        <li
                          key={`${message.id}-${String(index)}`}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr auto',
                            gap: 10,
                            padding: '9px 12px',
                            borderTop: index === 0 ? 'none' : '1px solid var(--grid)',
                          }}
                        >
                          <span style={{ minWidth: 0 }}>
                            <span className="set-mono" style={{ fontSize: 11.5 }}>
                              {attachment.filename}
                            </span>
                            {attachment.reason != null ? (
                              <span className="set-hint" style={{ display: 'block' }}>
                                {attachment.reason}
                              </span>
                            ) : null}
                          </span>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              color:
                                attachment.outcome === 'ingested'
                                  ? 'var(--ok-ink)'
                                  : attachment.outcome === 'failed'
                                    ? 'var(--bad)'
                                    : 'var(--t3)',
                            }}
                          >
                            {OUTCOME_LABEL[attachment.outcome]}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))
            : null}
        </>
      ) : null}
    </Drawer>
  );
}
